<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="70%"
  >
    <v-form>
      <v-select
        v-model="model.name"
        :items="templateList"
        :label="$t('Template')"
        @change="loadTemplate"
        item-text="text"
        item-value="id"
      ></v-select>
      <v-row>
        <v-col cols="9" align="left">
          <v-select
            v-model="model.language"
            :items="languageList"
            :label="$t('Language')"
            @change="loadTemplate"
            item-text="text"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="2" align="left">
          <v-select
            v-show="emailFields.length > 0"
            v-model="selectedField"
            :items="emailFields"
            :label="$t('Fields')"
            item-text="text"
            item-value="id"
          ></v-select>
        </v-col>
        <v-col cols="1" align="left">
          <v-btn @click="addField" small color="primary" class="mt-5">
            <v-icon>mdi-plus</v-icon></v-btn
          >
        </v-col>
      </v-row>
      <div class="sql-editor">
        <tiptap-vuetify
          v-model="model.template"
          :extensions="extensions"
          v-if="!advancedMode"
        />
        <codemirror
          v-model="model.template"
          :options="cmOption"
          v-if="advancedMode"
        />
      </div>
    </v-form>
    <template slot="footer">
      <v-btn :loading="loading" color="warning" @click="test" class="ml-4">{{
        $t("Test")
      }}</v-btn>
      <v-switch
        class="ml-4"
        color="primary"
        v-model="advancedMode"
        :label="$t('AdvancedMode')"
      />
    </template>
  </base-modal>
</template>

<script>
import OrganizationService from '@/services/OrganizationService';
import Utils from '@/ecm/common/Utils';

import { codemirror } from 'vue-codemirror';
// import base style
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/css/css.js';
import 'codemirror/mode/xml/xml.js';
import 'codemirror/mode/htmlmixed/htmlmixed.js';
import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify';

export default {
    components: {
        codemirror,
        TiptapVuetify,
    },
    created() {
        OrganizationService.getEmailTemplateList().then((data) => {
            this.templateList = data;
        });

        OrganizationService.getEmailTemplateLanguageList().then((data) => {
            this.languageList = data;
            this.languageList.unshift({ id: '', text: this.$t('All') });
        });
    },
    data() {
        return {
            modal: {
                title: this.$t('Templates'),
                key: 'emailTemplateModal',
            },
            open: false,
            showPassword: false,
            selectedField: null,
            model: {
                name: 'AccountRecoverPasswordTemplate',
                language: 'fr-fr',
                template: '',
            },
            advancedMode: false,
            languageList: [],
            templateList: [],
            loading: false,
            settings: null,
            extensions: [
                History,
                Blockquote,
                Link,
                Underline,
                Strike,
                Italic,
                ListItem,
                BulletList,
                OrderedList,
                [
                    Heading,
                    {
                        options: {
                            levels: [1, 2, 3],
                        },
                    },
                ],
                Bold,
                HorizontalRule,
                Paragraph,
                HardBreak,
            ],
            cmOption: {
                tabSize: 4,
                styleActiveLine: true,
                lineNumbers: true,
                line: true,
                mode: 'text/html',
            },
        };
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        emailFields() {
            switch (this.model.name) {
            case 'AlertEmailStateChangeTemplate':
            case 'AlertEmailStateExpiredTemplate':
            case 'AlertEmailUserGeneratedTemplate':
            case 'AlertEmailContentTypeTemplate':
                return [
                    { id: '%RECIPIENT_FULLNAME%', text: this.$t('RecipientFullname') },
                    { id: '%DOCUMENTS_LINK%', text: this.$t('Link') },
                ];

            case 'DistributionSharedLinkTemplate':
                return [
                    { id: '%RECIPIENT_FULLNAME%', text: this.$t('RecipientFullname') },
                    { id: '%SENDER_FULLNAME%', text: this.$t('SenderFullname') },
                    { id: '%DOCUMENTS_LINK%', text: this.$t('Link') },
                ];
            case 'OrganizationQuotaExceededTemplate':
                return [];
            case 'OrganizationQuotaWarningTemplate':
                return [{ id: '%ACTUAL_STORAGE_USE%', text: this.$t('StorageUse') }];
            case 'AccountRecoverPasswordTemplate':
                return [
                    { id: '%RECIPIENT_LOGIN%', text: this.$t('Login') },
                    { id: '%RECIPIENT_PASSWORD%', text: this.$t('Password') },
                ];
            case 'AccountRecoverDeviceTemplate':
                return [{ id: '%RECOVER_DEVICE_URL%', text: this.$t('Link') }];
            case 'AccountActivationTemplate':
                return [
                    { id: '%RECIPIENT_LOGIN%', text: this.$t('Login') },
                    { id: '%ACTIVATION_URL%', text: this.$t('Link') },
                ];
            case 'CommentMentionTemplate':
                return [{ id: '%DOCUMENT_COMMENT%', text: this.$t('Comment') },
                        { id: '%DOCUMENT_COMMENT_LINK%', text: this.$t('CommentLink') }
                ];
            case 'AlertEmailStateAutoImportTemplate':
                return [
                    { id: '%AUTOIMPORT%', text: this.$t('AutoImport') },
                    { id: '%DATEERROR%', text: this.$t('DateError') },
                    { id: '%CONTENTTYPENAME%', text: this.$t('ContentTypeName') },
                    { id: '%RECIPIENT_FULLNAME%', text: this.$t('RecipientFullname') },
                ];
            default:
                return [];
            }
        },
    },
    methods: {
        addField() {
            if (!this.selectedField) return;
            this.model.template += this.selectedField;
        },
        test() {
            this.loading = true;
            var settings = Utils.object.toJS(this.settings);
            settings.Body = this.model.template;
            //replace all fields
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%RECIPIENT_FULLNAME%',
                'User Fullname'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%DOCUMENTS_LINK%',
                'https://doc-decm-mock-link.com'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%SENDER_FULLNAME%',
                'Sender Fullname'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%ACTUAL_STORAGE_USE%',
                '90'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%RECIPIENT_LOGIN%',
                'user.docecm'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%RECIPIENT_PASSWORD%',
                'abc123'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%RECOVER_DEVICE_URL%',
                'https://doc-decm-mock-link.com'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%ACTIVATION_URL%',
                'https://doc-decm-mock-link.com'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%ACTIVATION_URL%',
                'https://doc-decm-mock-link.com'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%AUTOIMPORT%',
                'OneDrive'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%DATEERROR%',
                '01/01/2024'
            );
            settings.Body = Utils.text.replaceAll(
                settings.Body,
                '%CONTENTTYPENAME%',
                'ContentTypeName'
            );

            OrganizationService.testEmailConfig(settings)
                .then((data) => {
                    Utils.toast.success(this.$t('EmailSent'));
                })
                .catch(() => {})
                .then(() => {
                    //always
                    this.loading = false;
                });
        },
        loadTemplate() {
            OrganizationService.getEmailTemplate(this.model.language, this.model.name)
                .then((data) => {
                    this.model.template = data.template;
                })
                .catch(() => {});
        },
        saveItem() {
            var model = {
                Language: this.model.language,
                FileName: this.model.name,
                Content: this.model.template,
            };

            if (!this.checkFiltersAddedCorrectly(model)) return;

            this.modalInstance.progress(true);
            OrganizationService.saveEmailTemplate(model)
                .then((result) => {
                    if (result) {
                        Utils.toast.success(this.$t('EmailTemplate') + ' ' + this.$t('SavedSuccessfully'));
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.modalInstance.progress(false);
                });
        },
        show(settings) {
            this.loadTemplate();
            this.open = true;
            this.settings = settings;
        },
        hide() {
            this.open = false;
        },
        checkFiltersAddedCorrectly(model) {
            const requiredFields = this.emailFields.map(field => field.id);
            const missingFields = requiredFields.filter(field => !model.Content.includes(field));
            if (missingFields.length > 0) {
                Utils.toast.error(this.$t('Site.Organization.EmailTemplates.MissingFields') + ' ' + missingFields.join(', '));
                return false;
            } else {
                return true;
            }
        }
    },
};
</script>

<style lang="scss">
.sql-editor {
  .CodeMirror {
    border: 1px solid #eee;
    height: auto !important;
    min-height: 500px !important;
    /*max-height: 500px;*/
    line-height: 20px;
  }
  .CodeMirror-scroll {
    min-height: 500px;
  }
}
</style>
